// One student's disc — ported from the prototype demo's student page
// (app/demo/students/[studentId]/page.jsx) into the Mosaico page frame.
// Reached from the project page (roster, matrix drill-down, student work),
// so it lives under the project: /projects/:id/students/:studentId.
//
// Before the project is assessed the disc is drawn blank (same curriculum,
// no scores) so the presenter can show the shape of what will be assessed.
// The page carries its own "Assess with AI" button: it starts the project's
// run with THIS student first, so the bloom happens on the disc in view.
import { Anchor, Avatar, Badge, Box, Button, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { IconChevronLeft, IconSparkles } from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { ARC_AI_GRADIENT } from 'themes/ArcTheme'
import { Disc, DiscPopover } from '../components/disc'
import SkillsSidebar from '../components/features/SkillsSidebar'
import { useProjectDemoState } from '../data/demoState'
import { projectIdForStudent, useDiscSkills, useIsStudentGraded } from '../data/hooks'
import { demoProjectPath, findDemoProjectByCardId } from '../data/projects'
import { startAssessment } from '../data/simulation'
import { AssessmentProgress } from './AssessmentProgress'
import { useBloom } from './useBloom'
import { useArcDiskDemoEnabled, useArcDiskDemoLoading } from '../useArcDiskDemo'
import '../styles/arcdisk.css'
import '../styles/arcdisk-tailwind.css'

type DiscArea = { id?: string; name: string; color?: string }

const STUDENTS_LIST = '/teacher/manage-students'

const StudentPageInner = ({ cardId, adProjectId, studentId }: { cardId: string; adProjectId: string; studentId: number }) => {
  const { t } = useTranslation()
  // Reached from the Students tab (?from=students) or from the project.
  // The primary back link returns the way the viewer came; the other
  // destination stays one click away.
  const [params] = useSearchParams()
  const fromStudents = params.get('from') === 'students'
  const [clicked, setClicked] = useState<unknown>(null)
  const graded = useIsStudentGraded(studentId)
  const { status } = useProjectDemoState(adProjectId)
  const bloom = useBloom(graded)
  // The progress panel sits above the disc, so when this student's scores
  // land (bloom starts) bring the disc into view — that is the moment the
  // demo is built around.
  const discRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (graded && status === 'grading') {
      requestAnimationFrame(() => discRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    }
  }, [graded, status])
  const { data, error } = useDiscSkills(studentId)

  const studentName = data?.meta?.student_name || 'Student'
  const firstName = studentName.split(/\s+/)[0]
  const initials = studentName
    .split(/\s+/)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  const areas = (data?.curriculum ?? []) as DiscArea[]

  return (
    <>
      <Group spacing='lg'>
        <Anchor
          component={Link}
          to={fromStudents ? STUDENTS_LIST : demoProjectPath(cardId)}
          size='md'
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <IconChevronLeft size={18} />
          {fromStudents ? t('arcdisk_demo.back_to_students') : t('arcdisk_demo.back_to_project')}
        </Anchor>
        <Anchor
          component={Link}
          to={fromStudents ? demoProjectPath(cardId) : STUDENTS_LIST}
          size='sm'
          color='dimmed'
        >
          {fromStudents ? t('arcdisk_demo.view_project') : t('arcdisk_demo.all_students')}
        </Anchor>
      </Group>
      <Paper radius='md' withBorder shadow='xs' p={{ base: 'md', md: 'xl' }} mt='xs'>
        <Group position='apart' align='center'>
          <Group spacing='md' noWrap>
            <Avatar radius='xl' size={52} color='violet' variant='filled'>
              {initials}
            </Avatar>
            <Stack spacing={2}>
              <Title order={2}>{studentName}</Title>
              <Text size='sm' color='dimmed'>
                {graded
                  ? `${data?.meta?.graded_skills ?? 0} ${t('arcdisk_demo.student_assessed')}${
                    data?.meta?.avg_score ? ` · ${t('arcdisk_demo.typical_level')} ${data.meta.avg_score}` : ''
                  }`
                  : t('arcdisk_demo.not_assessed')}
              </Text>
            </Stack>
          </Group>
          <Group spacing='sm' noWrap>
            {graded ? (
              <Badge color='violet' variant='light' size='lg' leftSection={<IconSparkles size={12} />}>
                {t('arcdisk_demo.assessed')}
              </Badge>
            ) : (
              <Badge color={status === 'grading' ? 'yellow' : 'gray'} variant='light' size='lg'>
                {status === 'grading' ? t('arcdisk_demo.assessing') : t('arcdisk_demo.not_assessed')}
              </Badge>
            )}
            {status !== 'graded' && (
              <Tooltip label={t('arcdisk_demo.assess_student_hint', { name: firstName })} withArrow position='bottom'>
                <Button
                  size='md'
                  radius='xl'
                  leftIcon={<IconSparkles size={18} />}
                  onClick={() => startAssessment(adProjectId, studentId)}
                  disabled={status !== 'ungraded'}
                  loading={status === 'grading'}
                  styles={{
                    root: {
                      background: ARC_AI_GRADIENT,
                      border: 0,
                      '&:not([data-disabled]):hover': { filter: 'brightness(0.95)' },
                    },
                  }}
                >
                  {status === 'grading' ? t('arcdisk_demo.assessing') : t('arcdisk_demo.assess')}
                </Button>
              </Tooltip>
            )}
          </Group>
        </Group>

        {status !== 'ungraded' && (
          <Box mt='lg'>
            <AssessmentProgress adProjectId={adProjectId} />
          </Box>
        )}

        <Box mt='lg' className='ds arcdisk-root'>
          {error ? (
            <p className='text-[15px] text-muted-foreground'>{error.message}</p>
          ) : !areas.length ? (
            <p className='text-[15px] text-muted-foreground'>No curriculum is mapped for {studentName} yet.</p>
          ) : (
            <div className='grid gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_360px]'>
              <div ref={discRef} className='scroll-mt-20 rounded-2xl border border-border bg-card px-6 py-6 shadow-card'>
                <h2 className='text-[16px] font-semibold text-foreground'>What {firstName} can do</h2>
                <p className='mt-1.5 text-[14px] text-muted-foreground'>
                  Three rings of curriculum, innermost outwards: area, competency, skill.{' '}
                  {graded
                    ? 'Click an area or competency to zoom into it; click a skill cell to see the skill developmental descriptors inside it.'
                    : 'Every segment is white until the project is assessed.'}
                </p>
                <div className='mt-5 flex justify-center'>
                  {/* kind="content": the student tree's nodes carry a top-level
                      `kind`, unlike the class tree. */}
                  <Disc
                    curriculum={areas}
                    scores={[]}
                    width={560}
                    height={560}
                    kind='content'
                    gapByLevel={0.004}
                    reveal={bloom}
                    popoverComponent={(p: object) => <DiscPopover {...p} compactInnerRings />}
                    onClick={(node: unknown) => setClicked(node || null)}
                  />
                </div>
                <div className='mt-5 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5'>
                  <div className='flex flex-wrap gap-x-4 gap-y-1.5'>
                    {areas.map(area => (
                      <div key={area.id || area.name} className='flex items-center gap-1.5 text-[12px] text-muted-foreground'>
                        <span className='size-3 rounded-sm' style={{ background: area.color || '#cbd5e1' }} />
                        <span>{area.name}</span>
                      </div>
                    ))}
                  </div>
                  <span className='flex items-center gap-2.5 text-[12px] text-muted-foreground'>
                    <span>Color depth = skills developed (avg 3+)</span>
                    <span className='flex flex-col gap-0.5'>
                      <span className='h-2.5 w-28 rounded-full bg-[linear-gradient(to_right,var(--secondary),var(--primary))]' />
                      <span className='flex w-28 justify-between text-[10px] leading-none'>
                        <span>none</span>
                        <span>all</span>
                      </span>
                    </span>
                  </span>
                </div>
              </div>
              <SkillsSidebar clickedNode={clicked} studentId={studentId} studentName={studentName} />
            </div>
          )}
        </Box>
      </Paper>
    </>
  )
}

export const ArcDiskStudentPage = () => {
  const { id, studentId } = useParams()
  const enabled = useArcDiskDemoEnabled()
  const loading = useArcDiskDemoLoading()
  const ref = findDemoProjectByCardId(id)
  const sid = Number(studentId)
  if (loading) return null
  if (!enabled || !ref || !Number.isInteger(sid)) return <Navigate to='/arc_projects?tab=my' replace />
  // A student URL under the wrong project (hand-typed) goes to that project.
  if (projectIdForStudent(sid) !== ref.adProjectId) return <Navigate to={demoProjectPath(ref.cardId)} replace />
  return (
    <CurrentUserContextProvider>
      <Layout>
        <StudentPageInner cardId={ref.cardId} adProjectId={ref.adProjectId} studentId={sid} />
      </Layout>
    </CurrentUserContextProvider>
  )
}
